/**
 * Phone text -> `tel:` / `sms:` target, ONE rule for the client and the Cloud Functions.
 *
 * Moved unchanged from client/src/lib/contactHref.ts (ISS-571 follow-on, L-1156/L-1216) so the
 * server stops building its own dial targets: functions/src/ai-services/skills/core/callTeamMember.ts
 * prefixed "+" to every digit in the cell, so "831-555-1234 x12" dialled the international number
 * +831555123412. Pure (no DOM): the client's dialPhone, which needs `window`, stays in the client.
 *
 * @module lib/phone/phoneHref
 */

/**
 * Longest phone value read. Anything longer is not a phone number (the unauthenticated web form
 * stores whatever it is sent), so it gets no link at all: never a truncated, wrong number.
 */
const MAX_PHONE_CHARS = 256;

/**
 * The dash a word processor or phone keyboard substitutes for "-" (hyphen, non-breaking hyphen,
 * figure dash, en dash) and the no-break space: live leads carry "(831) 555‑1234" with U+2011 (2 of
 * 8,819 lead phones, measured 2026-10-15), which is the same number as with "-".
 */
const UNICODE_DASHES = new Set(['\u2010', '\u2011', '\u2012', '\u2013']);
/** Characters that may sit between the digit groups of ONE number. */
const isSeparator = (c: string): boolean =>
  c === ' ' || c === '\t' || c === '-' || c === '.' || c === '(' || c === ')' || c === '\u00a0' || UNICODE_DASHES.has(c);
const isSpace = (c: string): boolean => c === ' ' || c === '\t' || c === '\n' || c === '\r' || c === '\u00a0';
const isDigit = (c: string | undefined): boolean => c !== undefined && c >= '0' && c <= '9';
const isLetter = (c: string | undefined): boolean => c !== undefined && c.toLowerCase() !== c.toUpperCase();

/** A run of digits, and whether the gap BEFORE it held whitespace (a space) or only - . ( ). */
interface Group {
  digits: string;
  afterSpace: boolean;
  /** The group was opened by "(": "(831) 555-1234" marks 831 as an area code. */
  paren: boolean;
}

/**
 * The digit groups of the first number-like span: from the first digit (or a leading "+"), through
 * digits and separators, up to anything else (a letter, "/", ",", ";", "#", a second "+").
 * Returns the groups and whether the span began with "+". One pass, no regex.
 */
function readSpan(raw: string, start: number): { plus: boolean; groups: Group[] } {
  const groups: Group[] = [];
  let plus = false;
  let i = start;
  if (raw[i] === '+') {
    plus = true;
    i++;
  }
  let sawSpace = false;
  let sawParen = raw[start - 1] === '(';
  let current = '';
  for (; i < raw.length; i++) {
    const c = raw[i];
    if (isDigit(c)) {
      current += c;
      continue;
    }
    if (current) {
      groups.push({ digits: current, afterSpace: sawSpace, paren: sawParen });
      current = '';
      sawSpace = false;
      sawParen = false;
    }
    if (isSeparator(c) || c === '\n' || c === '\r') {
      if (isSpace(c)) sawSpace = true;
      if (c === '(') sawParen = true;
      continue;
    }
    break;
  }
  if (current) groups.push({ digits: current, afterSpace: sawSpace, paren: sawParen });
  return { plus, groups };
}

const lengths = (groups: Group[]): number[] => groups.map((g) => g.digits.length);
const startsWith = (have: number[], want: number[]): boolean => want.every((n, k) => have[k] === n);

/**
 * What follows a matched number may only be a SECOND number or an extension, never more of this
 * one: it must be absent, or begin after whitespace. "555-1234-567" and "8315551-234" carry digits
 * straight on with a dash, so which digits belong to the number is unknowable: no link.
 */
function endsCleanly(groups: Group[], used: number): boolean {
  const next = groups[used];
  return next === undefined || next.afterSpace;
}

/**
 * True when, after an "x" / "X" / "#" marker, only an optional dot and spaces separate it from the next digit:
 * "x22", "x 22", "X. 22", "# 22". The marker then opens an extension (or an ambiguous area code): see phoneHref.
 * @param raw the phone text
 * @param from the index just after the marker
 */
function markerThenDigit(raw: string, from: number): boolean {
  let j = from;
  if (raw[j] === '.') j++;
  while (raw[j] === ' ') j++;
  return isDigit(raw[j]);
}

/**
 * The link for a span's digit groups, or '' (see phoneHref for the shapes).
 * @param scheme tel or sms
 * @param plus the span began with "+"
 * @param groups its digit groups
 */
function matchNumber(scheme: 'tel' | 'sms', plus: boolean, groups: Group[]): string {
  if (groups.length === 0) return '';
  const lens = lengths(groups);
  const first = groups[0].digits;
  const join = (from: number, to: number): string => groups.slice(from, to).map((g) => g.digits).join('');
  // "+1" + 10 digits, only for a real area code: no North American area code starts with 0 or 1.
  const nanp = (ten: string): string => (ten[0] >= '2' ? `${scheme}:+1${ten}` : '');
  // After a local number, a space may lead only to nothing or a complete second number:
  // "8315551 234" or "555 1234 567" could be one longer number.
  const tailOk = (used: number): boolean =>
    used >= groups.length || matchNumber(scheme, false, groups.slice(used)) !== '';

  // A written "+" is linked only for country code 1; other countries need per-country rules.
  if (plus && !first.startsWith('1')) return '';
  if (first === '1') {
    // 1-3-3-4 is a complete number whatever follows; an unbroken 10 must end cleanly.
    if (startsWith(lens, [1, 3, 3, 4])) return nanp(join(1, 4));
    if (startsWith(lens, [1, 10]) && endsCleanly(groups, 2)) return nanp(join(1, 2));
    return '';
  }
  if (plus) {
    // "+1831..." written together, or "+1831 555 1234": the leading group holds the 1 itself.
    if (first.length === 11 && endsCleanly(groups, 1)) return nanp(first.slice(1));
    if (startsWith(lens, [4, 3, 4])) return nanp(join(0, 3).slice(1));
    return '';
  }
  if (first.startsWith('0')) return '';
  if (first.length === 11 && first.startsWith('1') && endsCleanly(groups, 1)) return nanp(first.slice(1));
  // A complete North American number: whatever follows (a dash extension included) is not part of it.
  if (startsWith(lens, [3, 3, 4])) return nanp(join(0, 3));
  if (first.length === 10) return endsCleanly(groups, 1) ? nanp(first) : '';
  // A 7-digit local number; a local exchange never starts with 0 or 1 either.
  if (first[0] < '2') return '';
  if (startsWith(lens, [3, 4]) && endsCleanly(groups, 2)) return tailOk(2) ? `${scheme}:${join(0, 2)}` : '';
  if (first.length === 7 && endsCleanly(groups, 1)) return tailOk(1) ? `${scheme}:${first}` : '';
  return '';
}

/**
 * `tel:` / `sms:` target for the FIRST number in a phone cell, or '' when there is none.
 *
 * STRICT ON PURPOSE (review rounds 4-7, 2026-10-12/14). The same helper builds every `tel:` link in
 * CRM, Recruiting and Support through `InlineEditField`, and a hand-written parser that guesses at
 * other countries' groupings kept dialling numbers nobody wrote. So a link is built only for the
 * shapes that can be read with certainty, and everything else shows as text with no link (a wrong
 * dial is worse than none):
 *  - a North American number: 3-3-4 digit groups (an optional leading "1" or "+1" group), or the
 *    same 10 / 11 digits unbroken -> "+1XXXXXXXXXX";
 *  - a 7-digit local number: 3-4 groups or 7 unbroken -> dialled as written, with no "+".
 * The number must END there: nothing may follow but whitespace and a second number or an extension
 * ("x22", "ext 22", "or 831 ...", "cell 831 ..."). A dash or dot carrying on into more digits
 * ("831-555-1234-12" excepted: a complete North American number is never ambiguous) gives no link.
 * A 3-4 local number followed by a 4-digit group ("138 1234 5678") could be one longer number, so
 * it gives no link too.
 *
 * NOT linked (text only): any number written with "+" and a country code other than 1, any number
 * starting with 0 / 00 / 011, and every other length or grouping. Reading those correctly needs
 * per-country length rules (a phone-number library), not a guess.
 *
 * A leading "ext" word before any digit ("ext 12 831-555-1234") marks a standalone first digit group
 * as the extension, which is skipped. Values over MAX_PHONE_CHARS get no link. Callers render no
 * link for '': an empty href reloads the page.
 */
export function phoneHref(scheme: 'tel' | 'sms', phone: string | undefined | null): string {
  const raw = phone ?? '';
  if (raw.length > MAX_PHONE_CHARS) return '';
  let start = 0;
  let extensionFirst = false;
  while (start < raw.length && !isDigit(raw[start]) && raw[start] !== '+') {
    const word = !isLetter(raw[start - 1]);
    if (word && raw.slice(start, start + 3).toLowerCase() === 'ext') extensionFirst = true;
    if (word && (raw[start] === 'x' || raw[start] === 'X' || raw[start] === '#') && markerThenDigit(raw, start + 1)) {
      extensionFirst = true;
    }
    start++;
  }
  if (start >= raw.length) return '';
  const { plus, groups } = readSpan(raw, start);
  const href = matchNumber(scheme, plus, groups);
  // A leading "ext" / "x" before a standalone first group: that group may be the extension
  // ("ext 12 555-123-4567") or the area code ("ext 204 555-1234" could be either). Read it both
  // ways; when both readings give a number and they differ, it is ambiguous: no link. A group
  // in parentheses ("Extension (831) 555-1234") is an area code, never an extension.
  if (extensionFirst && !plus && groups.length > 1 && groups[1].afterSpace && !groups[0].paren) {
    const skipped = matchNumber(scheme, false, groups.slice(1));
    if (href && skipped && href !== skipped) return '';
    return href || skipped;
  }
  return href;
}

/**
 * The number to TEXT for a phone cell: its first North American number as "+1XXXXXXXXXX", or '' when
 * there is none. The SMS composer accepts only a clean 10/11-digit US number, so a cell such as
 * "831-555-1234 x12" or "(831) 555-1234 / (408) 555-9999" (12 or 20 raw digits) disabled it while
 * Call still worked; this is what the composer is handed instead (ISS-571 review). A 7-digit local
 * number has no area code to text, so it gives ''.
 * @param phone the phone text as stored
 */
export function smsRecipient(phone: string | undefined | null): string {
  const href = phoneHref('sms', phone);
  return href.startsWith('sms:+1') ? href.slice('sms:'.length) : '';
}
